import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import FormTextBox from './FormTextBox';
import ICommonFormProps from '../interfaces/ICommonFormProps';
import FormElement from '../FormElement';
import { FilterOptionsState } from '@material-ui/lab';
import { Paper } from '@material-ui/core';
import { ISelectOptions } from '../interfaces/IDynamicFormRow';

const filter = createFilterOptions();

/**
 * pass it through an array of items and it can do a search as you type
 * we can even use this for debounce on an API
 * @param props.formProps.selectOptions array of items
 */
const FormAutoComplete = (props: ICommonFormProps) => {
    const { t } = useTranslation();
    const textBoxRef = useRef<HTMLInputElement>();
    const [value, setValue] = useState<any>([]);
    useEffect(() => {
        if (props.value) {
            setValue(props.value);
        }
        // eslint-disable-next-line
    }, [props.value]);
    
    const filterOption = useCallback((options: ISelectOptions[], params: FilterOptionsState<any>) => {
        const filtered = filter(options, params);
        return filtered;
    }, []);
    if (props.formProps?.selectOptions === undefined) {
        console.warn('You are missing select options');
    }
    const onSelect = (ev: React.ChangeEvent<{}>, value: any) => {
        /**Removes Text after you have selected an option */
        if (textBoxRef && textBoxRef.current) {
            textBoxRef.current.value = '';
        }
        if (Array.isArray(value)) {
            /**Checks if the value is unique */
            //@ts-ignore
            const newValue = [...new Set(value.map((v) => v[props.formProps?.selectKeyValue || 'value']))];

            if (newValue.length === value.length) {
                setValue(value);
            } else {
                return;
            }
        } else {
            setValue(value);
        }
        if (props.setValue) {
            props.setValue(props.keyName, value, { shouldDirty: true });
            if (props.formProps?.triggerValidationOnChange && props.triggerValidation) {
                props.triggerValidation(props.keyName);
            }
        }

        if (props.errors) {
            props.triggerValidation!(props.keyName);
        }
    };

    /**Props for autocomplete*/
    const propsObject = {
        options: props.formProps?.selectOptions || [],
        getOptionLabel: props.formProps?.getOptionLabel
            ? props.formProps?.getOptionLabel
            : (option: ISelectOptions) => option.name || '',
        value: value,
        loading: props.formProps?.loading,
        renderInput: (params: any) => (
            <FormTextBox
                inputProperties={{ ...params, ...props.inputProperties }}
                keyName={props.keyName}
                value={''}
                label={props.label}
                register={textBoxRef as any}
                errors={props?.errors}
            />
        ),
        onChange: onSelect,
        name: props.keyName,
        'data-testid': 'autocomplete',
        disabled: props.disabled,
        filterOption,
        className: props.className,
        renderOption: props.formProps?.renderOption ? props.formProps?.renderOption : undefined,
        getOptionDisabled: (option: any) => {
            if (option?.disabled) {
                return true;
            }
            return false;
        },
        onInputChange: props.formProps?.onInputChange ?? undefined,
    };
    /** Multiple only takes true, and nothing else so i can't do multiple as dynamic, so we're left with this below :(*/
    return (
        <FormElement {...props}>
            {props?.formProps?.multiple === true ? (
                <Autocomplete
                    noOptionsText={t('misc.noOptions')}
                    multiple
                    PaperComponent={(props) => <Paper {...props} elevation={4} />}
                    {...propsObject}
                />
            ) : (
                <Autocomplete
                    noOptionsText={t('misc.noOptions')}
                    PaperComponent={(props) => <Paper {...props} elevation={4} />}
                    {...propsObject}
                />
            )}
        </FormElement>
    );
};

export default FormAutoComplete;
